import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import AvatarCircle from '../components/AvatarCircle';
import UserList from '../components/UserList';
import PendingFollowRequests from '../components/PendingFollowRequests';
import { useAuthCheck } from '../util/utils';
import Toast from '../components/Toast';
import PrivacyPreferences from "./PrivacyPreferences";

const MeComponent = ({ api }) => {

    const location = useLocation()
    const navigate = useNavigate()

    const queryParams = new URLSearchParams(location.search)
    const tab = queryParams.get("tab") || 'details'

    const [toast, setToast] = useState(null)

    const [followers, setFollowers] = useState([])
    const [followedUsers, setFollowedUsers] = useState([])
    const [pending, setPending] = useState([])
    const [activeTab, setActiveTab] = useState(tab);
    const { token, logout } = useAuth()
    const [isEditing, setIsEditing] = useState(false);
    const [birthdayModalOpen, openBirthdayModal] = useState(false)
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);
    const [isPrivacyPopupOpen, setPrivacyPopupOpen] = useState(false)

    // reference state
    const [userInfo, setUserInfo] = useState({
        name: '',
        email: '',
        username: '',
        birthMonth: null,
        birthDay: null,
        private: false
    });

    // dirty state 
    const [birthMonth, setBirthMonth] = useState(1);
    const [birthDay, setBirthDay] = useState(1);


    const colors = [
        "bg-red-500", "bg-blue-500", "bg-green-500", "bg-yellow-500",
        "bg-purple-500", "bg-pink-500", "bg-teal-500", "bg-gray-500"
    ];

    const [selectedColor, setSelectedColor] = useState(colors[0]);

    const handleEditToggle = () => {
        setIsEditing(!isEditing);
    };

    const handleCancelEdit = () => {
        setIsEditing(false)
    }

    const handleSaveBirthday = async () => {
        try {
            const res = await api().updateBirthday(token, {
                birthDay: birthDay,
                birthMonth: birthMonth
            })

            if (res.status === 200) {
                fetchMe()
                setToast({ message: `Your birthday has been changed!` })
            }
        } catch (error) {

        }

        setIsEditing(false)
        openBirthdayModal(false);
    }


    const handleLogout = () => {
        logout()
    };

    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    const fetchMe = async () => {
        try {
            let resp = await api().getMe(token)
            if (resp.status === 200) {
                setUserInfo((prev) => ({
                    ...prev,
                    name: `${resp.data.firstName} ${resp.data.lastName}`,
                    email: resp.data.email,
                    username: resp.data.username,
                    birthMonth: resp.data.birthMonth,
                    birthDay: resp.data.birthDay,
                    private: resp.data.isPrivate,
                }));
                setBirthDay(resp.data.birthDay)
                setBirthMonth(resp.data.birthMonth)
            }
        } catch (e) { }
    }

    useAuthCheck()

    useEffect(() => {
        const listWhoIFollow = async () => {
            try {
                let resp = await api().listWhoIFollow(token)
                if (resp.status === 200) {
                    const formattedUsers = resp.data.map((follow) => {
                        const { firstName, lastName, username, birthMonth, birthDay } = follow.other;
                        const birthdayDate = `${birthMonth}/${birthDay}`;
                        return {
                            id: follow._id,
                            name: `${firstName} ${lastName}`,
                            username: username,
                            caption: birthdayDate,
                        };
                    });
                    setFollowedUsers(formattedUsers)
                }
            } catch (e) { }
        }; if (token) listWhoIFollow()
    }, [])

    useEffect(() => {
        const listMyFollowers = async () => {
            try {
                let resp = await api().listMyFollowers(token)
                if (resp.status === 200) {
                    const formattedUsers = resp.data.map((follow) => {
                        const { firstName, lastName, username } = follow.creator;
                        return {
                            id: follow._id,
                            name: `${firstName} ${lastName}`,
                            username: username,
                            caption: username,
                        };
                    });
                    setFollowers(formattedUsers)
                }
            } catch (e) { }
        }
        if (token) listMyFollowers()
    }, [token])

    useEffect(() => {
        if (token) fetchMe()
    }, [token])

    return (
        <div className="bg-gradient-to-b from-blue-400 to-green-700 min-h-screen flex flex-col pt-20 items-center py-8">
            <div className="bg-white shadow-lg rounded-lg w-full max-w-md p-6">
                {/* Profile Picture */}
                <div className="flex flex-col items-center">
                    <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                        <AvatarCircle letters={userInfo.name} />
                    </div>
                    <h2 className="text-2xl font-semibold mt-4">{userInfo.name}</h2>
                    <p className="text-gray-600">{userInfo.email}</p>
                </div>

                <div className="max-w-4xl mx-auto p-4">
                    {/* Tab Navigation */}
                    <div className="flex border-b">
                        <button
                            onClick={() => handleTabClick('details')}
                            className={`py-2 px-4 ${activeTab === 'details' ? 'border-b-2 border-blue-500' : ''}`}
                        >
                            Profile
                        </button>
                        <button
                            onClick={() => handleTabClick('followed')}
                            className={`py-2 px-4 ${activeTab === 'followed' ? 'border-b-2 border-blue-500' : ''}`}
                        >
                            {`Following (${followedUsers.length})`}
                        </button>
                        <button
                            onClick={() => handleTabClick('followers')}
                            className={`py-2 px-4 ${activeTab === 'followers' ? 'border-b-2 border-blue-500' : ''}`}
                        >
                            {`Followers (${followers.length})`}
                        </button>
                        {userInfo.private ? <button
                            onClick={() => handleTabClick('requests')}
                            className={`py-2 px-4 ${activeTab === 'requests' ? 'border-b-2 border-blue-500' : ''}`}
                        >
                            Requests
                        </button> : <div />}
                    </div>
                </div>

                {/* Bio Section */}
                {activeTab === 'details' && (
                    <div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">About me</h3>
                            <h4>{userInfo.username}</h4>
                            <h4>My birthday is on {userInfo.birthMonth}/{userInfo.birthDay}</h4>
                        </div>
                        {/* 
                        <h3 className="text-lg font-semibold mb-2">Choose a Profile Color</h3>
                        <div className="flex justify-center gap-3">
                            {colors.map((color) => (
                                <div
                                    key={color}
                                    className={`w-10 h-10 rounded-full cursor-pointer border-4 ${color} ${selectedColor === color ? "border-black" : "border-transparent"}`}
                                    onClick={() => setSelectedColor(color)}
                                />
                            ))}
                        </div> */}


                        {/* Editable Name and Email */}
                        <div className="mt-6">
                            <label className="block text-sm font-medium text-gray-800">Name</label>

                            <p className="mt-1 text-gray-700">{userInfo.name}</p>
                        </div>
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-800">Email</label>

                            <p className="mt-1 text-gray-700">{userInfo.email}</p>
                        </div>


                        {/* Buttons */}
                        <div className="mt-6">
                            {isEditing ? (
                                <>
                                    <button
                                        onClick={() => openBirthdayModal(true)}
                                        className="w-full mb-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        Change Birthday
                                    </button>
                                    <div className="flex space-x-2">

                                        <button
                                            onClick={handleCancelEdit}
                                            className="w-full px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                                        >
                                            Cancel
                                        </button>
                                    </div>


                                </>
                            ) : (
                                <button
                                    onClick={handleEditToggle}
                                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Edit profile
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    setPrivacyPopupOpen(true)
                                }}
                                className="w-full px-4 mt-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Edit privacy & preferences
                            </button>
                        </div>


                        {/* Logout Button */}
                        <div className="mt-6">
                            <button
                                onClick={() => setShowLogoutDialog(true)}
                                className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                            >
                                Log out
                            </button>
                        </div>

                    </div>
                )}

                {activeTab === 'followed' && (
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800">Following</h2>
                        {followedUsers.length === 0 ? (
                            <p>You are not following any users yet.</p>
                        ) : (
                            <UserList users={followedUsers} size={"size-12"} textColor={"text-gray-800"} />
                        )}
                    </div>
                )}

                {activeTab === 'followers' && (
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800">Followers</h2>
                        {followers.length === 0 ? (
                            <p>You have no followers</p>
                        ) : (
                            <UserList users={followers} size={"size-12"} textColor={"text-gray-800"} />
                        )}
                    </div>
                )}

                {activeTab === 'requests' && (
                    <div>
                        <PendingFollowRequests api={api} />
                    </div>
                )}

                {/* Logout Confirmation Dialog */}
                {showLogoutDialog && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="bg-white p-6 rounded-lg shadow-lg w-80">
                            <h3 className="text-lg font-semibold text-gray-800">Log out</h3>
                            <p className="mt-2 text-gray-600">Are you sure you want to log out?</p>
                            <div className="mt-4 flex justify-end gap-4">
                                <button
                                    onClick={() => setShowLogoutDialog(false)}
                                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleLogout}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                                >
                                    Log out
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {birthdayModalOpen && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white p-6 rounded-lg shadow-lg w-80">
                            <h3 className="text-xl font-medium mb-4">Select Your Birthday</h3>

                            {/* Month & Day Select Inputs */}
                            <div className="flex space-x-2">
                                <select
                                    className="w-1/2 p-2 border rounded-md"
                                    value={birthMonth}
                                    onChange={(e) => setBirthMonth(Number(e.target.value))}
                                >
                                    {Array.from({ length: 12 }, (_, i) => (
                                        <option key={i + 1} value={i + 1}>
                                            {new Date(2000, i, 1).toLocaleString("en-US", { month: "long" })}
                                        </option>
                                    ))}
                                </select>

                                <select
                                    className="w-1/2 p-2 border rounded-md"
                                    value={birthDay}
                                    onChange={(e) => setBirthDay(Number(e.target.value))}
                                >
                                    {Array.from({ length: 31 }, (_, i) => (
                                        <option key={i + 1} value={i + 1}>
                                            {i + 1}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Buttons */}
                            <div className="flex justify-end mt-4 space-x-2">
                                <button
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                    onClick={handleSaveBirthday}
                                >
                                    Save
                                </button>
                                <button
                                    className="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500"
                                    onClick={() => openBirthdayModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {isPrivacyPopupOpen && (<PrivacyPreferences api={api} onClose={(data) => {
                    setPrivacyPopupOpen(false)
                    if (data.message) {
                        setToast(data)
                    }
                    fetchMe()
                }} />)}
            </div>
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </div>
    );
};


const Me = ({ api }) => (
    <div>
        <Header api={api} />
        <MeComponent api={api} />
    </div>
)


export default Me;
